import * as React from "react";
import { useEffect, useState } from "react";
import capitalize from "lodash/capitalize";
import moment from "moment";
import { useStore } from "stores/connect";
import { ConfigurationsActions, ConfigurationsStore } from "stores/configurations/ConfigurationsStore";
import { getConfig } from "components/configurations/helpers";
import { ConfigurationType } from "components/configurations/ConfigurationTypes";
import { Button, FormGroup, HelpBlock, BootstrapModalForm } from "components/bootstrap";
import { IfPermitted, TimeUnitInput } from "components/common";
import Spinner from "components/common/Spinner";
import { getValueFromInput } from "util/FormsUtils";
import Input from "components/bootstrap/Input";
import { extractDurationAndUnit } from "components/common/TimeUnitInput";
const TIME_UNITS = ["HOURS", "MINUTES", "SECONDS"];
const DEFAULT_CATCH_UP_WINDOW = 36e5;
const DEFAULT_CONFIG = {
  events_search_timeout: 6e4,
  events_notification_retry_period: 3e5,
  events_notification_default_backlog: 50,
  events_catchup_window: DEFAULT_CATCH_UP_WINDOW,
  events_notification_tcp_keepalive: false
};
const EventsConfig = () => {
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [viewConfig, setViewConfig] = useState(DEFAULT_CONFIG);
  const [formConfig, setFormConfig] = useState(DEFAULT_CONFIG);
  const configuration = useStore(ConfigurationsStore, (state) => state == null ? void 0 : state.configuration);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    ConfigurationsActions.list(ConfigurationType.EVENTS_CONFIG).then(() => {
      const config = getConfig(ConfigurationType.EVENTS_CONFIG, configuration);
      setViewConfig(config);
      setFormConfig(config);
      setLoaded(true);
    });
  }, [configuration]);
  const openModal = () => {
    setShowConfigModal(true);
  };
  const closeModal = () => {
    setShowConfigModal(false);
    setFormConfig(viewConfig);
  };
  const saveConfig = () => {
    ConfigurationsActions.update(ConfigurationType.EVENTS_CONFIG, formConfig).then(() => {
      closeModal();
    });
  };
  const propagateChanges = (key, value) => {
    setFormConfig({ ...formConfig, [key]: value });
  };
  const searchTimeoutValidator = (milliseconds) => milliseconds >= 1e3;
  const notificationsRetryValidator = (milliseconds) => milliseconds >= 0;
  const onSearchTimeoutUpdate = (nextValue, nextUnit, enabled) => {
    const durationInMs = enabled ? moment.duration(nextValue, nextUnit).asMilliseconds() : 0;
    if (searchTimeoutValidator(durationInMs)) {
      propagateChanges("events_search_timeout", durationInMs);
    }
  };
  const onRetryPeriodUpdate = (nextValue, nextUnit, enabled) => {
    const durationInMs = enabled ? moment.duration(nextValue, nextUnit).asMilliseconds() : 0;
    if (notificationsRetryValidator(durationInMs)) {
      propagateChanges("events_notification_retry_period", durationInMs);
    }
  };
  const onBacklogUpdate = (event) => {
    const value = getValueFromInput(event.target);
    propagateChanges("events_notification_default_backlog", value);
  };
  const onCatchUpWindowUpdate = (nextValue, nextUnit, nextEnabled) => {
    if (formConfig.events_catchup_window === 0 && nextEnabled) {
      propagateChanges("events_catchup_window", DEFAULT_CATCH_UP_WINDOW);
      return;
    }
    const catchupWindowinMs = nextEnabled ? moment.duration(nextValue, nextUnit).asMilliseconds() : 0;
    propagateChanges("events_catchup_window", catchupWindowinMs);
  };
  const onNotificationTcpKeepAliveUpdate = (event) => {
    const value = getValueFromInput(event.target);
    propagateChanges("events_notification_tcp_keepalive", value);
  };
  const titleCase = (str) => capitalize(str);
  if (!loaded || !viewConfig) {
    return /* @__PURE__ */ React.createElement(Spinner, null);
  }
  const eventsSearchTimeout = (config) => extractDurationAndUnit(config.events_search_timeout, TIME_UNITS);
  const eventsNotificationRetryPeriod = (config) => extractDurationAndUnit(config.events_notification_retry_period, TIME_UNITS);
  const eventsCatchupWindow = (config) => extractDurationAndUnit(config.events_catchup_window, TIME_UNITS);
  const eventsNotificationDefaultBacklog = (config) => config.events_notification_default_backlog;
  const eventsNotificationTcpKeepalive = (config) => config.events_notification_tcp_keepalive;
  return /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement("h2", null, "Events Configuration"), /* @__PURE__ */ React.createElement("dl", { className: "deflist" }, /* @__PURE__ */ React.createElement("dt", null, "Search Timeout:"), /* @__PURE__ */ React.createElement("dd", null, eventsSearchTimeout(viewConfig).duration, " ", titleCase(eventsSearchTimeout(viewConfig).unit)), /* @__PURE__ */ React.createElement("dt", null, "Notification Retry:"), /* @__PURE__ */ React.createElement("dd", null, eventsNotificationRetryPeriod(viewConfig).duration, " ", titleCase(eventsNotificationRetryPeriod(viewConfig).unit)), /* @__PURE__ */ React.createElement("dt", null, "Notification Backlog:"), /* @__PURE__ */ React.createElement("dd", null, eventsNotificationDefaultBacklog(viewConfig)), /* @__PURE__ */ React.createElement("dt", null, "Catch Up Window:"), /* @__PURE__ */ React.createElement("dd", null, eventsCatchupWindow(viewConfig).duration > 0 ? eventsCatchupWindow(viewConfig).duration : "disabled", " ", eventsCatchupWindow(viewConfig).duration > 0 ? titleCase(eventsCatchupWindow(viewConfig).unit) : ""), /* @__PURE__ */ React.createElement("dt", null, "TCP keep-alive probes:"), /* @__PURE__ */ React.createElement("dd", null, eventsNotificationTcpKeepalive(viewConfig) ? "enabled" : "disabled")), /* @__PURE__ */ React.createElement(IfPermitted, { permissions: "clusterconfigentry:edit" }, /* @__PURE__ */ React.createElement(Button, { bsStyle: "info", bsSize: "xs", onClick: openModal }, "Edit configuration")), showConfigModal && formConfig && /* @__PURE__ */ React.createElement(
    BootstrapModalForm,
    {
      show: true,
      title: "Update Events System Configuration",
      onSubmitForm: saveConfig,
      onCancel: closeModal,
      submitButtonText: "Update configuration"
    },
    /* @__PURE__ */ React.createElement("fieldset", null, /* @__PURE__ */ React.createElement(FormGroup, { controlId: "search-timeout-field" }, /* @__PURE__ */ React.createElement(
      TimeUnitInput,
      {
        label: "Search Timeout",
        update: onSearchTimeoutUpdate,
        value: eventsSearchTimeout(formConfig).duration,
        unit: eventsSearchTimeout(formConfig).unit,
        units: TIME_UNITS,
        required: true
      }
    ), /* @__PURE__ */ React.createElement(HelpBlock, null, "Amount of time after which an Elasticsearch query is interrupted. (Minimum timeout is 1s)")), /* @__PURE__ */ React.createElement(FormGroup, { controlId: "notifications-retry-field" }, /* @__PURE__ */ React.createElement(
      TimeUnitInput,
      {
        label: "Notifications retry period",
        update: onRetryPeriodUpdate,
        value: eventsNotificationRetryPeriod(formConfig).duration,
        unit: eventsNotificationRetryPeriod(formConfig).unit,
        units: TIME_UNITS,
        required: true
      }
    ), /* @__PURE__ */ React.createElement(HelpBlock, null, "Amount of time after which a failed notification is resend. (Minimum is 0 or immediate retry)")), /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "notification-backlog-field",
        type: "number",
        onChange: onBacklogUpdate,
        label: "Default notifications backlog size",
        help: "Amount of log messages included in a notification by default.",
        value: eventsNotificationDefaultBacklog(formConfig),
        min: "0",
        required: true
      }
    ), /* @__PURE__ */ React.createElement(FormGroup, { controlId: "catch-up-window" }, /* @__PURE__ */ React.createElement(
      TimeUnitInput,
      {
        label: "Catch up window size",
        update: onCatchUpWindowUpdate,
        value: eventsCatchupWindow(formConfig).duration,
        unit: eventsCatchupWindow(formConfig).unit,
        enabled: eventsCatchupWindow(formConfig).duration > 0,
        units: TIME_UNITS
      }
    ), /* @__PURE__ */ React.createElement(HelpBlock, null, 'If Event processor execution is behind schedule, queries on older data will be run with this window size to speed up processing. (If the "search within the last" setting of an event definition is greater, this setting will be ignored)')), /* @__PURE__ */ React.createElement(FormGroup, { controlId: "notification-tcp-keepalive-field" }, /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "notification-tcp-keepalive-field",
        label: "Send TCP keep-alive probes for notification connections",
        type: "checkbox",
        onChange: onNotificationTcpKeepAliveUpdate,
        checked: eventsNotificationTcpKeepalive(formConfig)
      }
    ), /* @__PURE__ */ React.createElement(HelpBlock, null, "If enabled, http connections for notifications will send TCP keep-alive probes")))
  ));
};
export default EventsConfig;
